import type { FilaCartera } from '@/lib/supabase/tipos-bd'
import { pesos } from '@/shared/utilidades/formato'

import { EDADES, edadDe, type ClaveEdad } from './edades'
import type { RecaudoDelPeriodo } from './useCartera'
import estilos from './PaginaCartera.module.css'

/**
 * LO QUE SE DEBE Y QUÉ TAN VIEJO ES.
 *
 * Tres bloques: lo que se debe HOY, lo recaudado EN EL PERIODO escogido
 * —partido en bancos y efectivo (Johan: «¿cuánto recaudé en bancos o en
 * efectivo?»)— y la barra de edades. Cada tramo de la leyenda es un botón: tocarlo deja en la lista solo
 * las cuentas de esa edad (Johan: «más versátil»). Volver a tocarlo, o
 * «Todas las edades», quita el filtro.
 */
export function ResumenCartera({
  cuentas,
  recaudo,
  nombrePeriodo,
  edad,
  onEdad,
}: {
  cuentas: FilaCartera[]
  /** Lo recaudado en el periodo escogido; null mientras carga. */
  recaudo: RecaudoDelPeriodo | null
  /** «28 sept – 4 oct», «octubre de 2026»… */
  nombrePeriodo: string
  edad: ClaveEdad | null
  onEdad: (e: ClaveEdad | null) => void
}) {
  const abiertas = cuentas.filter((c) => Number(c.saldo) > 0)
  const porCobrar = abiertas.reduce((s, c) => s + Number(c.saldo), 0)
  const r = recaudo ?? { total: 0, bancos: 0, efectivo: 0, otro: 0, pagos: 0 }
  const partes = [
    { clave: 'bancos', rotulo: 'Bancos', valor: r.bancos },
    { clave: 'efectivo', rotulo: 'Efectivo', valor: r.efectivo },
    ...(r.otro > 0 ? [{ clave: 'otro', rotulo: 'Otro', valor: r.otro }] : []),
  ]
  const porEdad = EDADES.map((e) => {
    const de = abiertas.filter((c) => edadDe(c.dias).clave === e.clave)
    return { ...e, valor: de.reduce((s, c) => s + Number(c.saldo), 0), cuantas: de.length }
  })

  return (
    <section className={estilos.resumen} aria-label="Resumen de la cartera">
      <div className={`${estilos.cifra} ${estilos.cifraFuerte}`}>
        <p className={estilos.cifraRotulo}>Por cobrar hoy</p>
        <p className={estilos.cifraValor}>{pesos(porCobrar)}</p>
        <p className={estilos.cifraPie}>
          {abiertas.length} {abiertas.length === 1 ? 'cuenta abierta' : 'cuentas abiertas'}
        </p>
      </div>

      <div className={estilos.cifra}>
        <p className={estilos.cifraRotulo}>Recaudado · {nombrePeriodo}</p>
        <p className={estilos.cifraValor}>{recaudo ? pesos(r.total) : '…'}</p>
        {/* Bancos y efectivo, con su barra: de un vistazo, por dónde entró. */}
        <div className={estilos.medios} aria-hidden="true">
          {r.total > 0 ? (
            partes
              .filter((x) => x.valor > 0)
              .map((x) => (
                <span
                  key={x.clave}
                  className={estilos[`medio_${x.clave}`]}
                  style={{ flexGrow: x.valor }}
                />
              ))
          ) : (
            <span className={estilos.medioVacio} />
          )}
        </div>
        <ul className={estilos.mediosLeyenda}>
          {partes.map((x) => (
            <li key={x.clave}>
              <i className={estilos[`medio_${x.clave}`]} />
              {x.rotulo}
              <b>{pesos(x.valor)}</b>
            </li>
          ))}
        </ul>
        <p className={estilos.cifraPie}>
          {r.pagos} {r.pagos === 1 ? 'pago' : 'pagos'} en el periodo
        </p>
      </div>

      <div className={estilos.edades}>
        <div className={estilos.edadesTope}>
          <p className={estilos.cifraRotulo}>Por edades · desde que se finalizó</p>
          {edad && (
            <button type="button" className={estilos.quitarFiltro} onClick={() => onEdad(null)}>
              Todas las edades ×
            </button>
          )}
        </div>
        <div className={estilos.edadesBarra} aria-hidden="true">
          {porEdad
            .filter((e) => e.valor > 0)
            .map((e) => (
              <span
                key={e.clave}
                className={`${estilos[e.clave]} ${edad && edad !== e.clave ? estilos.apagada : ''}`}
                style={{ flexGrow: e.valor }}
                title={`${e.rotulo}: ${pesos(e.valor)}`}
              />
            ))}
          {porCobrar === 0 && <span className={estilos.edadVacia} />}
        </div>
        <div className={estilos.edadesLeyenda} role="group" aria-label="Filtrar por edad">
          {porEdad.map((e) => (
            <button
              key={e.clave}
              type="button"
              className={`${estilos.edadBoton} ${estilos[e.clave]} ${
                edad === e.clave ? estilos.edadViva : ''
              }`}
              aria-pressed={edad === e.clave}
              onClick={() => onEdad(edad === e.clave ? null : e.clave)}
              disabled={e.cuantas === 0 && edad !== e.clave}
            >
              <i className={estilos[e.clave]} />
              <span>{e.rotulo}</span>
              <b>{pesos(e.valor)}</b>
            </button>
          ))}
        </div>
      </div>
    </section>
  )
}
